import {
  BarChartOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDeleteMeetingMutation, useUpdateMeetingMutation } from '@/@generated/codegen/API';
import { ROUTES } from '@/routes/Routes';
import ConfirmModal from '@/ui/ConfirmModal/ConfirmModal';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { AdminMeetingListItemType } from '../columns';

type Props = {
  meeting: AdminMeetingListItemType;
  nonDeletableMeetings: string[];
  onDupeMeeting: (meeting: AdminMeetingListItemType) => void;
  onEditMeeting: (meeting: AdminMeetingListItemType) => void;
  onUserAccess: (meeting: AdminMeetingListItemType) => void;
};

export const AdminManageMeetingMenu = ({
  meeting,
  onDupeMeeting,
  onEditMeeting,
  onUserAccess,
}: Props) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const archiveDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const [mutationUpdateMeeting] = useUpdateMeetingMutation();
  const [mutationDeleteMeeting] = useDeleteMeetingMutation();

  const handleMeetingStats = (meetingID: string) => {
    navigate(`${ROUTES.MEETING_STATS}/${meetingID}`);
  };

  const handleArchiveMeeting = (meetingID: string) => {
    mutationUpdateMeeting({
      variables: {
        data: {
          id: meetingID,
          isArchived: true,
        },
      },
    }).catch((err) => {
      captureSentry({
        title: 'Error archiving meeting',
        error: err,
        detail: {
          meetingID,
        },
      });
      toast({
        title: 'Error archiving meeting',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    });
  };

  const handleDeleteMeeting = (meetingID: string) => {
    void mutationDeleteMeeting({
      variables: {
        where: {
          id: meetingID,
        },
      },
    });
  };

  return (
    <>
      <ConfirmModal
        title={
          <div>
            {t('admin.meeting.archive.confirmText')}
            <br />
            {t('meeting.header.confirmRestart')}
          </div>
        }
        onConfirm={() => handleArchiveMeeting(meeting.id)}
        okText={t('common.yes') ?? undefined}
        cancelText={t('common.no') ?? undefined}
        disclosure={archiveDisclosure}
      />
      <ConfirmModal
        title={
          <div>
            {t('admin.meeting.delete.confirmText')}
            <br />
            {t('meeting.header.confirmRestart')}
          </div>
        }
        onConfirm={() => handleDeleteMeeting(meeting.id)}
        okText={t('common.yes') ?? undefined}
        cancelText={t('common.no') ?? undefined}
        disclosure={deleteDisclosure}
      />
      <Menu>
        <MenuButton as={Button} size="xs" variant="secondary" borderRadius={4} aria-label="Options">
          <Text>Manage</Text>
        </MenuButton>
        <MenuList>
          <MenuItem icon={<BarChartOutlined />} onClick={() => handleMeetingStats(meeting.id)}>
            {t('admin.meetings.list.viewStats')}
          </MenuItem>
          <MenuItem icon={<CopyOutlined />} onClick={() => onDupeMeeting(meeting)}>
            {t('admin.meetings.list.duplicate')}
          </MenuItem>

          <MenuItem icon={<UserOutlined />} onClick={() => onUserAccess(meeting)}>
            {t('admin.meetings.list.userAccess')}
          </MenuItem>
          <MenuItem icon={<EditOutlined />} onClick={() => onEditMeeting(meeting)}>
            {t('common.edit')}
          </MenuItem>

          <MenuItem
            icon={<FolderOpenOutlined />}
            onClick={() => archiveDisclosure.onToggle()}
            isDisabled={meeting.isArchived}
          >
            {!meeting.startDate ? t('common.cancel') : t('common.archive')}
          </MenuItem>
          <MenuItem icon={<DeleteOutlined />} onClick={() => deleteDisclosure.onToggle()}>
            {t('common.delete')}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

export default AdminManageMeetingMenu;
